/**
 * When a thread has settled.
 *
 * "Settled" means the work is finished for now — not deleted, not hidden, just
 * no longer something you are waiting on. The sidebar parks settled threads in
 * a collapsed shelf so the active list stays short.
 *
 * The state is durable when available (`settledAt`/`settledOverride`) and keeps
 * the inactivity ladder as a backwards-compatible fallback for older rows.
 *
 * The blocking conditions come first and beat everything, including an explicit
 * settle. A thread waiting on your approval must stay visible even if you told
 * it to go away, because the alternative is work that silently stops with the
 * only affordance to resume it hidden behind a collapsed shelf.
 *
 * @module
 */

/** Days of silence after which a thread settles on its own. */
export const DEFAULT_AUTO_SETTLE_AFTER_DAYS = 3;

/**
 * Statuses that keep a thread in the active list, for two different reasons.
 *
 * `running` is busy: settling it would hide work in progress. `stuck` has
 * *failed* and is waiting for a person, which is the loudest reason of all to
 * stay visible — and it is the one the inactivity path would otherwise catch,
 * because a thread that broke three days ago has by definition been silent for
 * three days. Silence is the symptom, not evidence that it is finished.
 */
const ACTIVE_STATUSES: ReadonlySet<string> = new Set(["running", "stuck"]);

/** Statuses that mean the thread is finished, whatever its timestamps say. */
const TERMINAL_STATUSES: ReadonlySet<string> = new Set(["merged"]);

export interface SettledThreadInput {
  readonly status?: string | null | undefined;
  readonly archivedAt?: string | null | undefined;
  readonly settledAt?: string | null | undefined;
  readonly settledOverride?: "settled" | "active" | null | undefined;
  readonly hasPendingApprovals?: boolean | undefined;
  readonly hasPendingUserInput?: boolean | undefined;
  readonly latestUserMessageAt?: string | null | undefined;
  readonly updatedAt?: string | null | undefined;
  readonly createdAt?: string | null | undefined;
}

export interface SettledOptions {
  /** Quantise to the minute; see `useNowMinute` for why. */
  readonly now: string;
  /** Null disables the inactivity path entirely, leaving only explicit settles. */
  readonly autoSettleAfterDays: number | null;
}

/** The most recent moment anything happened, or null when nothing is dated. */
export function latestActivityAt(thread: SettledThreadInput): string | null {
  const candidates = [thread.latestUserMessageAt, thread.updatedAt, thread.createdAt].filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((newest, value) =>
    Date.parse(value) > Date.parse(newest) ? value : newest,
  );
}

/**
 * Whether `thread` reads as settled at `now`.
 *
 * Order matters and is the contract:
 *   1. explicitly settled   -> settled
 *   2. blocked or running    -> active when no filing choice exists
 *   4. terminal status      -> settled
 *   5. silent for N days    -> settled
 *   6. otherwise            -> active
 */
export function effectiveSettled(thread: SettledThreadInput, options: SettledOptions): boolean {
  // 1. Explicit durable override. An active override is a deliberate restore
  // and must win over terminal/inactivity fallbacks.
  if (thread.settledOverride === "active") return false;
  if (thread.settledOverride === "settled") return true;

  // 2. Attention and execution state remain visible independently. They stop
  // automatic filing, but do not undo a person's explicit filing choice.
  if (thread.hasPendingApprovals === true) return false;
  if (thread.hasPendingUserInput === true) return false;
  if (typeof thread.status === "string" && ACTIVE_STATUSES.has(thread.status)) return false;

  // 4. Durable automatic settlement. Archived rows remain settled for
  // backwards compatibility, but archive is now the later release state.
  if (thread.settledAt != null) return true;
  if (thread.archivedAt != null) return true;

  // 5. Finished work stays out of the way without waiting for the clock.
  if (typeof thread.status === "string" && TERMINAL_STATUSES.has(thread.status)) return true;

  // 6. Silence. Null disables the path rather than meaning zero — "never
  // auto-settle" has to be expressible, and a 0-day rule would settle
  // everything the moment it stopped running.
  const { autoSettleAfterDays } = options;
  if (autoSettleAfterDays === null || autoSettleAfterDays <= 0) return false;

  const activityAt = latestActivityAt(thread);
  if (activityAt === null) return false;

  const activityMs = Date.parse(activityAt);
  const nowMs = Date.parse(options.now);
  if (!Number.isFinite(activityMs) || !Number.isFinite(nowMs)) return false;

  const elapsedDays = (nowMs - activityMs) / 86_400_000;
  return elapsedDays >= autoSettleAfterDays;
}

/**
 * Whether the Settle/Un-settle menu item should read as settled.
 *
 * Narrower than `effectiveSettled`: the pending-approval/running exceptions
 * and the silence-based auto-settle path decide which shelf a row lands in,
 * but they are not a durable choice the menu can reverse, so they do not
 * belong in what the menu item offers. An explicit `settledOverride:
 * "active"` (the state Un-settle leaves behind) always wins over a durable
 * settledAt/archivedAt, matching `effectiveSettled`'s rule order above.
 */
export function isDurablySettled(
  thread: Pick<SettledThreadInput, "settledOverride" | "settledAt" | "archivedAt">,
): boolean {
  return (
    thread.settledOverride !== "active" &&
    (thread.settledOverride === "settled" || thread.settledAt != null || thread.archivedAt != null)
  );
}
